/**
 * Mentorship concept glossary — the single source of truth for what
 * every concept word means inside the mentorship workspace.
 *
 * Purpose
 * -------
 * Definitions in UI copy drift fast: an "Insight" tooltip on one
 * surface and a slightly different "Insight" explainer on another
 * surface confuse both mentors and mentees. This module is the
 * one authoritative dictionary every consumer imports from. If a
 * definition changes, change it here and every surface follows.
 *
 * One SSOT, two consumer shapes
 * -----------------------------
 * This file used to ship a parallel `MENTORSHIP_GLOSSARY` constant
 * narrowed to the 9 concept terms. That parallel block is gone:
 * `GLOSSARY` is now the only record, and concept-only consumers
 * (signposting components, WelcomeOverlay glossary step)
 * import the `MentorshipTerm` subset alias defined at the bottom of
 * this file to constrain themselves to the 9 concept keys without
 * forking the data.
 *
 * Consumers
 * ---------
 *   - `MentorshipWelcomeOverlay` step 3 (glossary table)
 *   - `SectionHelpButton` popover content
 *   - `GuidanceHelperCard` sub-panel copy
 *   - `NeedsAttentionView` section-legend text (always-visible, redesign §6)
 *   - `ConceptChip` / signposting primitives — use `MentorshipTerm`
 *   - Anywhere else a concept needs one-sentence explanation
 *
 * Exports — two SSOT records
 * --------------------------
 * `GLOSSARY` — concept + view noun definitions (shortLabel, pluralLabel,
 *   oneSentenceDefinition, expandedDefinition).
 * `STATUS_VOCABULARY` — universal workflow-state definitions per redesign
 *   §14 (shortLabel, oneSentenceDefinition, colorTone). See the
 *   "Universal Status Language SSOT" section below.
 *
 * Style guide
 * -----------
 * - `shortLabel`: 1-2 words. The human-readable name of the concept.
 * - `oneSentenceDefinition`: one calm sentence, present-tense, no
 *   exclamation. Plain English. Avoids jargon. Concept terms keep
 *   this under 110 chars so a single tooltip row never wraps; the
 *   7 view entries may run longer because their surfaces have room.
 * - `expandedDefinition`: one short paragraph (max ~3 sentences)
 *   that gives concrete shape — either an explicit worked example
 *   (INSIGHT, PROJECT) or a lifecycle walkthrough that shows what
 *   the concept looks like in practice (SUBMISSION, REPORT, REVIEW,
 *   FEEDBACK, MEETING, DISPUTE). Used wherever the surface has room
 *   for more context (welcome overlay, help popover).
 *
 * Adding new terms: add the key in BOTH `GlossaryTermKey` and
 * `GLOSSARY`. If the new term is a concept (not a view), also add it
 * to the `MentorshipTerm` subset alias. The exhaustiveness test
 * guards against drift.
 */

export type LegacyGlossaryEntry = {
    /** Human-readable singular label (e.g. "Concern"). The canonical
     *  UI word for this concept — all surfaces import from here, never
     *  hard-code. Required, not optional, so a missing label is a
     *  build error rather than a silent fallback. */
    shortLabel: string;
    /** Human-readable plural label (e.g. "Concerns"). Used for rail
     *  section titles, count chips ("3 Concerns"), and other contexts
     *  that need the count form. For uncountable mass nouns (e.g.
     *  "Feedback"), this matches `shortLabel`. */
    pluralLabel: string;
    oneSentenceDefinition: string;
    expandedDefinition: string;
};

/**
 * Two key-naming categories live in this union:
 *
 * 1. Concept-with-instances (singular form):
 *    INSIGHT, PROJECT, SUBMISSION, REPORT, REVIEW, FEEDBACK,
 *    RECOMMENDATION, MEETING, DISPUTE.
 *    These name countable things; `pluralLabel` carries the plural.
 *
 * 2. Single-named-view (natural form):
 *    AT_RISK, NEEDS_ATTENTION, TRENDS, ENGAGEMENT, TODAY, COACHING,
 *    MENTALITY.
 *    These name panes / surfaces that are not countable instances;
 *    `pluralLabel` typically equals `shortLabel`.
 */
export type GlossaryTermKey =
    | 'INSIGHT'
    | 'PROJECT'
    | 'SUBMISSION'
    | 'REPORT'
    | 'REVIEW'
    | 'FEEDBACK'
    | 'RECOMMENDATION'
    | 'MEETING'
    | 'DISPUTE'
    // ── Single-named views (mentor + global panes) ──────────────
    | 'AT_RISK'
    | 'NEEDS_ATTENTION'
    | 'TRENDS'
    | 'ENGAGEMENT'
    | 'TODAY'
    | 'COACHING'
    | 'MENTALITY';

export const GLOSSARY: Record<GlossaryTermKey, LegacyGlossaryEntry> = {
    INSIGHT: {
        shortLabel: 'Insight',
        pluralLabel: 'Insights',
        oneSentenceDefinition:
            'A short suggestion a mentor leaves for a mentee — no client or project attached.',
        expandedDefinition:
            'An Insight is a single-prompt nudge from a mentor to one mentee, tagged with a type (behavioral, technical, or risk) and a priority. It is not scoped to a client or a multi-week project, and mentees can mark each one Helpful or Dismiss. The same record appears to mentees under the label "Recommendation" and is sometimes shown as "Tip" in in-context explainers.',
    },
    PROJECT: {
        shortLabel: 'Project',
        pluralLabel: 'Projects',
        oneSentenceDefinition:
            'A scoped engagement with a client — has dates, a description, and a register of risks to investigate.',
        expandedDefinition:
            'A formal engagement frame the mentor sets up for the mentee. Projects have a name, a client, start/end dates, and a list of risks (each with a trigger, impact, and mitigation). Example: "Q2 engagement with ACME Health to map clinical-trial regulatory risks." Mentees see active projects in their Projects pane.',
    },
    SUBMISSION: {
        shortLabel: 'Submission',
        pluralLabel: 'Submissions',
        oneSentenceDefinition:
            'A piece of work or reflection a mentee submits, scored first by AI and then reviewed by their mentor.',
        expandedDefinition:
            'A Submission is the artifact a mentee turns in against a module and quarter for evaluation. It moves through four states — submitted, assessed (AI-scored against a rubric), reviewed (mentor has added qualitative feedback), and finalized — and once finalized it becomes a Report. Mentee-facing screens often refer to a Submission as a reflection; both words point to the same entity.',
    },
    REPORT: {
        shortLabel: 'Report',
        pluralLabel: 'Reports',
        oneSentenceDefinition:
            'The finalised, versioned record of a reviewed submission — agreed score, level, and mentor notes.',
        expandedDefinition:
            'A Report is produced when a mentor finalises their review of a submission. It bundles the agreed score, recommended level, strengths, gaps, next actions, and mentor notes into a versioned record that supersedes earlier drafts. Reports are the artefact mentees open in the feedback take-over, and the anchor a mentee disputes when raising a concern.',
    },
    REVIEW: {
        shortLabel: 'Review',
        pluralLabel: 'Reviews',
        oneSentenceDefinition:
            "A mentor's qualitative pass over a mentee's submission, with per-question feedback.",
        expandedDefinition:
            'Reviews are what mentors do in the Mentor Submission Review take-over: read each question, add feedback, adjust the AI-suggested score, then finalise. Finalising a review produces a Report.',
    },
    FEEDBACK: {
        shortLabel: 'Feedback',
        // Uncountable mass noun — "Feedbacks" is not standard English.
        // The plural collapses to the singular form by design.
        pluralLabel: 'Feedback',
        oneSentenceDefinition:
            "The mentor's written commentary attached to a finalised report — the prose part, alongside score and level.",
        expandedDefinition:
            "Feedback is the qualitative commentary a mentor writes during a review and that travels with the finalised report: strengths, gaps, and next actions in prose form. It is distinct from the score and level, which are separate fields on the same report. The mentee opens it in the Feedback take-over to read why the report came out the way it did and what to work on next.",
    },
    RECOMMENDATION: {
        shortLabel: 'Recommendation',
        pluralLabel: 'Recommendations',
        oneSentenceDefinition:
            "How mentees see the Insights their mentor has surfaced — same data, mentee-facing name.",
        expandedDefinition:
            "Recommendations are the mentee-side view of Insights. The data is identical; the label differs because the act of giving (Insight) and the act of receiving (Recommendation) have different natural English words. When mentors and mentees talk to each other, both names refer to the same thing.",
    },
    MEETING: {
        shortLabel: 'Meeting',
        pluralLabel: 'Meetings',
        oneSentenceDefinition:
            'A scheduled 1:1 catchup between a mentor and a mentee, with a cadence, status, and audit history.',
        expandedDefinition:
            "A Meeting is the persistent record of a mentor-mentee catchup: one row per pair, holding the next scheduled date, the cadence (weekly through quarterly or ad hoc), and a status of scheduled, occurred, missed, or cancelled. Rescheduling updates the same row and appends to its history rather than creating a new one. Two weeks before the next scheduled date, a pre-meeting summary of the mentee's recent chatbot dialogue is generated for the mentor.",
    },
    DISPUTE: {
        // Data-model name: 'dispute'. UI label: 'Concern' (singular)
        // / 'Concerns' (plural). The rename is presentation-only;
        // every UI consumer imports from this entry, never hard-codes
        // the word 'Dispute' in user-facing copy. See docs/10-of-10-plan.md
        // §8.2 for the rationale.
        shortLabel: 'Concern',
        pluralLabel: 'Concerns',
        oneSentenceDefinition:
            "A mentee's formal flag that a finalised report needs another look.",
        expandedDefinition:
            'A Concern is the mentee\'s formal objection to a finalised Report — its score, recommended level, or feedback — raised with a written reason. It enters the mentor\'s Concerns queue with a state of open, resolved, or withdrawn; the mentor closes it by re-reviewing, replying, or adjusting the report. Stored in the data model as "dispute"; surfaced everywhere in the UI as "Concern" for calmer tone.',
    },

    // ── Single-named view terms (rail-section labels) ────────────────
    // These name surfaces, not countable instances. The audit
    // identified that At Risk / Needs Attention / Trends / Engagement
    // were missing from the glossary even though each is a rail tab
    // label; without entries, the SectionHelpButton ? popovers had
    // nothing to source from. These 7 entries close that gap and
    // unblock the ConceptChip primitive (next WS-DISC-1 sub-slice).

    AT_RISK: {
        shortLabel: 'At risk',
        pluralLabel: 'At risk',
        oneSentenceDefinition:
            "A mentee whose recent activity shows a warning signal — idle ≥14 days, an overdue submission, or an open concern.",
        expandedDefinition:
            "Mentees flagged as at risk surface in the mentor's At Risk pane and contribute to the Today \"Needs attention\" KPI. The signal blends three sources: idle days since last submission, overdue reviews past SLA, and unresolved concerns. The intent is to catch a mentee drifting before their next scheduled meeting, not to grade them.",
    },
    NEEDS_ATTENTION: {
        shortLabel: 'Needs attention',
        pluralLabel: 'Needs attention',
        oneSentenceDefinition:
            "The master ranked queue of every action waiting on a mentor — overdue reviews, open concerns, and idle-mentee follow-ups combined.",
        expandedDefinition:
            "The Needs Attention pane is the unfiltered superset of the Today digest. Items are deduped per entity and ranked by priority. A mentor working from this queue clears overdue work; the Today pane is the curated top of the same list, capped at the 8 highest-priority items.",
    },
    TRENDS: {
        shortLabel: 'Trends',
        pluralLabel: 'Trends',
        oneSentenceDefinition:
            "Aggregate metrics across a mentor's full book of mentees — totals, active counts, submissions this week, median review turnaround, and at-risk count.",
        expandedDefinition:
            "The Trends pane scans every mentee under a mentor and rolls up engagement statistics. Numbers update as submissions land and reports finalise. Distinct from Engagement, which is per-mentee narrative summarisation produced by the AI — Trends is book-wide numerics.",
    },
    ENGAGEMENT: {
        shortLabel: 'Engagement',
        pluralLabel: 'Engagement',
        oneSentenceDefinition:
            "An AI-generated narrative summary of a mentee's recent dialogue with the chatbot, surfaced two weeks before each scheduled meeting.",
        expandedDefinition:
            "Two weeks before a scheduled meeting, the mentor sees an Engagement summary covering the prior 90 days of mentee–chatbot dialogue: top topics, issues raised, effective prompts, ignored prompts, and a suggested focus for the meeting. Distinct from Trends (book-wide numbers) — Engagement is per-mentee narrative.",
    },
    TODAY: {
        shortLabel: 'Today',
        pluralLabel: 'Today',
        oneSentenceDefinition:
            "The mentor's daily triage digest — the top 8 ranked actions to clear today, plus KPIs for the broader queue.",
        expandedDefinition:
            "Today is the recommended landing pane for mentors. The ranked action list combines overdue reviews, open concerns, and idle mentees; the top 8 appear here and the full list lives in Needs Attention. KPI tiles show the day's at-risk total, reviews pending, and open concerns.",
    },
    COACHING: {
        shortLabel: 'Coaching',
        pluralLabel: 'Coaching',
        oneSentenceDefinition:
            "The mentorship workspace tab — covers reviews, feedback, projects, recommendations, and meetings for both mentors and mentees.",
        expandedDefinition:
            "Coaching is the top-level navigation tab that opens the mentorship workspace. Mentors see their queue, curation tools, and analytics; mentees see their home, submissions, projects, recommendations, and progression. Both roles share the same data model with role-gated views.",
    },
    MENTALITY: {
        shortLabel: 'Mentality',
        pluralLabel: 'Mentality',
        oneSentenceDefinition:
            "The AI coach panel — combines a mentor's curated risks, insights, and client profiles with a chat interface, reachable anywhere via the floating ✨ button.",
        expandedDefinition:
            "The Mentality overlay is a global AI assistant available across the whole app, not just the mentorship workspace. It surfaces situational guidance scoped to the current focus (e.g. \"Guidance for Sarah Chen\") and answers questions using the mentor's curated material. Drag the ✨ button anywhere; its position persists per-device.",
    },
};

// ────────────────────────────────────────────────────────────────────
// Concept-only consumer contract
//
// Most surfaces (rail panes, count chips, status badges) read from
// `GLOSSARY` keyed by the full `GlossaryTermKey` union. Signposting
// surfaces — workflow map, concept chip, KPI tooltips, welcome
// overlay glossary step — only ever talk about the 9 concept terms
// and want their props narrowed accordingly. The `MentorshipTerm`
// alias below is that narrower contract; `getGlossaryEntry` is a
// thin lookup over the same `GLOSSARY` record, typed to it.
// ────────────────────────────────────────────────────────────────────

// Subset alias for the 9 concept terms — consumers that only need
// the concept vocabulary (signposting components, NeedsAttentionView
// count-chip tooltip, WelcomeOverlay glossary step) import this narrower
// type; legacy
// rail/pane consumers keep using GlossaryTermKey directly.
export type MentorshipTerm = Extract<GlossaryTermKey,
    'INSIGHT' | 'PROJECT' | 'SUBMISSION' | 'REPORT' | 'REVIEW'
    | 'FEEDBACK' | 'RECOMMENDATION' | 'MEETING' | 'DISPUTE'
>

// Re-exported entry shape so new consumers can name it without
// touching LegacyGlossaryEntry. Identical structure — same record.
export type GlossaryEntry = LegacyGlossaryEntry

// Single lookup helper over the unified GLOSSARY constant.
export const getGlossaryEntry = (term: MentorshipTerm): LegacyGlossaryEntry => GLOSSARY[term]

// ────────────────────────────────────────────────────────────────────
// Surface-copy slots
//
// Named copy constants for surfaces whose title/description is shared
// vocabulary but does not fit the per-term { shortLabel,
// oneSentenceDefinition, expandedDefinition } record shape used by
// `GLOSSARY`. These are stand-alone exports — one per slot — so
// surfaces import the exact string they render and a copy edit lands
// in one place. Add new slots below; keep them grouped by surface.
// ────────────────────────────────────────────────────────────────────

/** SettingsPane title for the mentor's submission review queue —
 *  rendered by `ReviewsPane`. Stays the rail-section 'reviews' key
 *  in URL hash; the user-facing copy is the calmer "Review Queue". */
export const SUBMISSION_QUEUE_TITLE = "Review Queue" as const
/** SettingsPane description for the mentor's submission review
 *  queue — pairs with `SUBMISSION_QUEUE_TITLE` on the same surface. */
export const SUBMISSION_QUEUE_DESCRIPTION = "Submissions awaiting your finalisation." as const

// ── WS-NAV-2 Phase 3-d FilterStrip labels ───────────────────────────
//
// The Unified Inbox FilterStrip renders a horizontal row of filter
// chips ("All", "Overdue", "Concerns", "Idle") above every consumer
// surface (NeedsAttention / Today / At-risk / Reviews). These labels
// are user-facing copy; route them through these named slots so a
// rename lands in one place. "Concerns" uses `GLOSSARY.DISPUTE`'s
// plural form for consistency with the rest of the surface; "All" /
// "Overdue" / "Idle" are FilterStrip-specific shorthand without a
// per-term GLOSSARY entry (they are filter facets, not concept nouns).

/** FilterStrip label for the "show every row regardless of kind"
 *  facet. The default selection — clicking it clears the kind set. */
export const FILTER_STRIP_ALL_LABEL = "All" as const

/** FilterStrip label for the "only overdue reviews" facet. Maps to
 *  the `overdue-review` IaV2Row kind. */
export const FILTER_STRIP_OVERDUE_LABEL = "Overdue" as const

/** FilterStrip label for the "only open concerns" facet. Maps to the
 *  `open-dispute` IaV2Row kind; the label mirrors
 *  `GLOSSARY.DISPUTE.pluralLabel` so the FilterStrip word matches the
 *  rest of the surface (rail badge, ReasonChip copy). */
export const FILTER_STRIP_CONCERNS_LABEL = GLOSSARY.DISPUTE.pluralLabel

/** FilterStrip label for the "only idle mentees" facet. Maps to the
 *  `idle-mentee` IaV2Row kind. */
export const FILTER_STRIP_IDLE_LABEL = "Idle" as const

// ── WS-NAV-2 Phase 3-e SinceLastVisit digest copy ───────────────────
//
// The SinceLastVisitDigest renders a compact pill above the
// UnifiedQueueFilterStrip on every ia-v2 consumer surface
// ("NeedsAttention", "Today", "At-risk", "Reviews"). Three slots:
//   - the "<n> new" prefix where n is the count of items newer than
//     the mentor's last-visit timestamp;
//   - the "since" connector that joins the count to the relative
//     time anchor (e.g. "2 hours ago", "yesterday");
//   - the screen-reader-only verbose hint, which spells the surface
//     name back ("on Today") so AT users hear which surface's count
//     just changed when aria-live='polite' announces an update.
//
// Centralising the three slots keeps the digest's copy edits in one
// place and lets the no-naked-concept-strings rule stay quiet — the
// strings live in glossary.ts, the call site reads them as named
// constants.

/** SinceLastVisitDigest pill prefix — "N new" where N is the count.
 *  Builds the pill body as `${count} ${LABEL}` so the final copy
 *  reads "3 new since 2 hours ago". */
export const SINCE_LAST_VISIT_NEW_LABEL = "new" as const

/** SinceLastVisitDigest connector — joins the count prefix to the
 *  relative-time anchor ("3 new since 2 hours ago"). */
export const SINCE_LAST_VISIT_SINCE_LABEL = "since" as const

// ── WS-NAV-2 Phase 3-f SavedViewPicker copy ─────────────────────────
//
// The SavedViewPicker dropdown lives BESIDE the UnifiedQueueFilterStrip
// on every ia-v2 consumer surface. It surfaces three sections:
//   - the trigger ("Saved Views") that opens the dropdown;
//   - the "+ New View" affordance that captures the current filter
//     selection as a named view;
//   - the per-row delete affordance ("Delete view") rendered next to
//     each saved view in the list.
//
// Centralising the slot copy keeps the picker's strings out of the
// component file so a calm rename lands in one place — the same
// pattern the FilterStrip + SinceLastVisitDigest already follow.

/** SavedViewPicker trigger label — opens the dropdown. Verbatim
 *  rendered on the button. */
export const SAVED_VIEWS_TRIGGER_LABEL = "Saved Views" as const

/** SavedViewPicker "save current selection" affordance label —
 *  rendered as the dropdown's last row, opens the inline name input. */
export const SAVED_VIEWS_NEW_LABEL = "New View" as const

/** SavedViewPicker per-row delete affordance label — rendered next to
 *  each saved view in the dropdown. Used for the icon button's
 *  aria-label so AT users hear "Delete view, Overdue + Concerns". */
export const SAVED_VIEWS_DELETE_LABEL = "Delete view" as const

/** SavedViewPicker name-input placeholder — calm hint copy inside the
 *  text input that appears when the mentor clicks "+ New View". */
export const SAVED_VIEWS_NAME_PLACEHOLDER = "Name this view" as const

/** SavedViewPicker save-confirm button label — rendered as the
 *  primary CTA next to the inline name input. */
export const SAVED_VIEWS_SAVE_LABEL = "Save" as const

/** SavedViewPicker save-cancel button label — rendered as the
 *  secondary CTA next to the inline name input. */
export const SAVED_VIEWS_CANCEL_LABEL = "Cancel" as const

/** SavedViewPicker empty-state copy — rendered inside the dropdown
 *  when no saved views exist for the current surface. */
export const SAVED_VIEWS_EMPTY_LABEL = "No saved views yet" as const

/** SavedViewPicker "manage saved views" footer affordance label —
 *  rendered as the dropdown's trailing footer item when an `onManage`
 *  callback is supplied. Opens a management surface (modal, panel,
 *  etc.) owned by the consumer. */
export const SAVED_VIEWS_MANAGE_LABEL = "Manage saved views" as const

/** SavedViewPicker colon separator rendered in the trigger label when
 *  an active view is selected: "Saved Views: {viewName}". Centralised
 *  here so a redesign rename lands in one place. */
export const SAVED_VIEWS_TRIGGER_SEPARATOR = ": " as const

// ── WS-NAV-5 j/k chain KbdLegend copy ───────────────────────────────
//
// The KbdLegend pill renders above the row list on every ia-v2 consumer
// surface ("NeedsAttention", "Today", "At-risk", "Reviews") to surface
// the j/k/Enter chain bound by `useJkRowChain`. Three string slots:
//   - the "j/k" pair (verbatim, not localised — the keycaps are
//     the keyboard's literal labels);
//   - the "navigate" verb the j/k pair maps onto;
//   - the "Enter open" pair that maps the Enter keycap onto the
//     row-open intent.
//
// Centralising the slot copy keeps the legend's strings out of the
// component file so a calm rename lands in one place — the same
// pattern the FilterStrip + SinceLastVisitDigest + SavedViewPicker
// already follow.

/** KbdLegend verb for the j/k key pair — "navigate". Rendered after
 *  the literal "j/k" keycaps. */
export const KBD_LEGEND_NAVIGATE_LABEL = "navigate" as const

/** KbdLegend verb for the Enter keycap — "open". Rendered after the
 *  literal "Enter" keycap so the mentor reads "Enter open". */
export const KBD_LEGEND_OPEN_LABEL = "open" as const

// ────────────────────────────────────────────────────────────────────
// Internal id constants for the DISPUTE concept
//
// The data-model name "dispute" leaks into infrastructure-internal
// identifiers — URL hash, rail section key, command-palette id /
// keywords, RecommendedAction kind discriminator. These IDs are NOT
// user-facing copy (the rendered label still routes through
// `GLOSSARY.DISPUTE.shortLabel` or `<ConceptChip term="DISPUTE" />`),
// but they MUST stay stable so existing bookmarks, deep-links, and
// stored UI state keep resolving (see MentorReviewDashboard §193-198
// comment for the bookmark-stability contract).
//
// Centralising them here keeps `chiefy/no-dispute-outside-service`
// clean at the call sites — consumers import the IDENTIFIER, the
// rule's regex only matches the literal word shape, and the
// glossary.ts file path is in the rule's ALLOWED_PATH_RE so the
// literal value can live here without self-flagging.
// ────────────────────────────────────────────────────────────────────

/** URL-hash route segment + RailLayout section key for the mentor
 *  Concerns pane. Internal identifier — DO NOT rename without a
 *  coordinated bookmark-migration plan. The rendered rail label
 *  flows through `GLOSSARY.DISPUTE.pluralLabel` ("Concerns"). */
export const DISPUTE_ROUTE_KEY = 'disputes' as const

/** URL-hash form of `DISPUTE_ROUTE_KEY` — the leading-hash literal
 *  passed to `navigateHash(...)` from the command palette + Today
 *  KPI tiles. Derived from `DISPUTE_ROUTE_KEY` so the two never drift. */
export const DISPUTE_ROUTE_HASH = `#${DISPUTE_ROUTE_KEY}` as const

/** RecommendedAction kind discriminator for the "open concern"
 *  branch of the Today / Needs Attention queues. Internal — the
 *  rendered CTA label is "Open concern" sourced from `GLOSSARY`. */
export const OPEN_DISPUTE_KIND = 'open-dispute' as const

/** Command-palette id for the mentor "Open Concerns" entry. Internal
 *  — the rendered label is "Open Concerns" sourced from `GLOSSARY`. */
export const COMMAND_ID_MENTOR_DISPUTES = 'mentor.disputes' as const

/** Command-palette keyword token for matching the Concerns command.
 *  Internal — matcher uses substring + word-boundary scoring; the
 *  raw "disputes" token preserves the legacy keyword for users who
 *  search by the data-name. */
export const COMMAND_KEYWORD_DISPUTES = 'disputes' as const

// ────────────────────────────────────────────────────────────────────
// Universal Status Language SSOT — per redesign §14
//
// Six canonical workflow states used across Submissions, Reviews,
// Reports, and Concerns. This block is the single place where state
// keys are mapped to their user-facing label, one-sentence semantics,
// and visual tone. Consumers import `getStatusDefinition` and pass a
// `StatusKey`; the component layer (StatusPill) does the rendering.
//
// Rules:
//   - Add a new state here ONLY — never coin a new string at a
//     call site. The `StatusKey` union is exhaustive by design.
//   - `colorTone` drives StatusPill's CSS class; it must match the
//     tone tokens in StatusPill.css.
//   - `oneSentenceDefinition` is included in the StatusPill aria-label
//     so keep it screen-reader-friendly (plain English, ≤ 110 chars).
// ────────────────────────────────────────────────────────────────────

export type StatusKey =
    | 'DRAFT'
    | 'SUBMITTED'
    | 'IN_REVIEW'
    | 'APPROVED'
    | 'REJECTED'
    | 'COMPLETED';

export type StatusTone = 'neutral' | 'info' | 'warning' | 'success' | 'danger';

export type StatusDefinition = {
    /** User-facing label, e.g. "In Review". */
    shortLabel: string;
    /** One-sentence semantics — also used in aria-label. */
    oneSentenceDefinition: string;
    /** Visual tone driving the StatusPill color. */
    colorTone: StatusTone;
};

export const STATUS_VOCABULARY: Record<StatusKey, StatusDefinition> = {
    DRAFT: {
        shortLabel: 'Draft',
        oneSentenceDefinition: 'Owner is still composing; no other party sees it.',
        colorTone: 'neutral',
    },
    SUBMITTED: {
        shortLabel: 'Submitted',
        oneSentenceDefinition: 'Sent to the counterparty; awaiting their action.',
        colorTone: 'info',
    },
    IN_REVIEW: {
        shortLabel: 'In Review',
        oneSentenceDefinition: 'Counterparty acknowledged and is actively working on a response.',
        colorTone: 'warning',
    },
    APPROVED: {
        shortLabel: 'Approved',
        oneSentenceDefinition: 'Finalised positively (Reviews scored; Concerns resolved in favour).',
        colorTone: 'success',
    },
    REJECTED: {
        shortLabel: 'Rejected',
        oneSentenceDefinition: 'Finalised negatively (Concerns dismissed; Submissions withdrawn).',
        colorTone: 'danger',
    },
    COMPLETED: {
        shortLabel: 'Completed',
        oneSentenceDefinition: 'Closed without further action — archive state.',
        colorTone: 'neutral',
    },
};

/** Type-safe accessor — compile-time exhaustive over StatusKey.
 *  Passing an unknown key is a TypeScript error, never a runtime
 *  silent fallback. */
export function getStatusDefinition(key: StatusKey): StatusDefinition {
    return STATUS_VOCABULARY[key];
}
